/**
 * LanguagePicker — searchable dropdown over ISO 639-1 language codes.
 *
 * Self-contained absolute-positioned dropdown (NOT Radix Popover) so it works
 * inside Sheet/Dialog without the pointer-events trap. Same pattern as
 * SearchablePersonPicker in ConsultingConsultants.tsx and our DatePicker.
 *
 * Props:
 *   value         — current ISO code (lowercase). '' means "no language set".
 *   onChange      — called with the new code (lowercase). Receiver decides
 *                   what '' means in its context (often "any language").
 *   allowedCodes  — if provided, restrict the dropdown to these codes only
 *                   (used on the Generate Document modal, where we only want
 *                   to offer languages that have an active template).
 *   includeBlank  — if true, show a "(any language)" entry at the top.
 *   id            — optional id for the trigger button (label htmlFor).
 *   disabled      — disable the trigger.
 *   placeholder   — text shown when no value selected.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { Input } from '@/components/ui/input'
import { ISO639, ISO639_BY_CODE, type LanguageEntry } from '@/lib/iso639'

type Props = {
  value: string
  onChange: (code: string) => void
  allowedCodes?: string[]
  includeBlank?: boolean
  id?: string
  disabled?: boolean
  placeholder?: string
}

export function LanguagePicker({
  value,
  onChange,
  allowedCodes,
  includeBlank = false,
  id,
  disabled = false,
  placeholder = 'Pick a language…',
}: Props) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const wrapperRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Outside-click and Escape close the dropdown
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      const w = wrapperRef.current
      if (w && !w.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    setTimeout(() => inputRef.current?.focus(), 0)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  // Source list: either the allowedCodes (in their original order with name
  // lookup, falling back to UPPERCASE for unknown codes), or the full ISO639.
  const sourceList: LanguageEntry[] = useMemo(() => {
    if (!allowedCodes) return ISO639
    const lc = allowedCodes.map((c) => (c || '').toLowerCase()).filter(Boolean)
    return lc.map((code) => ISO639_BY_CODE[code] || { code, name: code.toUpperCase() })
  }, [allowedCodes])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return sourceList
    return sourceList.filter(
      (l) =>
        l.code.toLowerCase().includes(q) ||
        l.name.toLowerCase().includes(q),
    )
  }, [sourceList, query])

  const v = (value || '').toLowerCase()
  const selectedEntry = v ? ISO639_BY_CODE[v] || { code: v, name: v.toUpperCase() } : null

  const triggerLabel = selectedEntry
    ? `${selectedEntry.code.toUpperCase()} — ${selectedEntry.name}`
    : includeBlank && !v
      ? '(any language)'
      : placeholder

  const empty = sourceList.length === 0

  return (
    <div ref={wrapperRef} className="relative">
      <button
        id={id}
        type="button"
        disabled={disabled || empty}
        onClick={() => setOpen((o) => !o)}
        className={`w-full inline-flex items-center justify-between rounded-md border border-input bg-background px-3 h-10 text-sm text-left ${
          disabled || empty ? 'opacity-60 cursor-not-allowed' : 'hover:bg-muted/30'
        }`}
      >
        <span className={selectedEntry ? 'text-foreground' : 'text-muted-foreground'}>
          {empty ? 'No languages available' : triggerLabel}
        </span>
        <span className="text-muted-foreground text-xs">▾</span>
      </button>
      {open && !empty && (
        <div className="absolute z-[60] mt-1 left-0 right-0 rounded-lg bg-popover shadow-md ring-1 ring-foreground/10">
          <div className="p-2 border-b">
            <Input
              ref={inputRef}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by code or name…"
              className="h-8 text-sm"
              autoFocus
            />
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            {includeBlank && !query.trim() && (
              <button
                type="button"
                onClick={() => { onChange(''); setOpen(false); setQuery('') }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-muted/50 italic text-muted-foreground ${
                  !v ? 'bg-muted/30' : ''
                }`}
              >
                (any language)
              </button>
            )}
            {filtered.length === 0 ? (
              <div className="px-3 py-3 text-sm text-muted-foreground italic">No matches.</div>
            ) : (
              filtered.map((l) => (
                <button
                  key={l.code}
                  type="button"
                  onClick={() => { onChange(l.code); setOpen(false); setQuery('') }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-muted/50 ${
                    l.code === v ? 'bg-muted/30' : ''
                  }`}
                >
                  <span className="font-mono text-xs text-muted-foreground mr-2">
                    {l.code.toUpperCase()}
                  </span>
                  <span className="text-foreground">{l.name}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}
